import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatHistorySidebar from './ChatHistorySidebar';
import { AppContext } from '../../context/AppContext';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import ProfileIcon from '../../assets/icon-profile-initals.svg?react';
import BellIcon from '../../assets/icon-bell-profile.svg?react';
import SandboxIcon from '../../assets/icon-sandbox.svg?react';
import LogoutIcon from '../../assets/icon-logout-profile.svg?react';
import { logout } from '@/services/authentication';
import ElysiaBrainIcon from '@/assets/icon-elysia-brain.svg?react';
import SettingsIcon from '@/assets/icon-settings-gear.svg?react';

import OpenLeftSidePanelIcon from '@/assets/icon-open-pane.svg?react';
import NewChatIcon from '../../assets/icon-topic.svg?react';
import './style.scss';
import { generateChatSessionId } from '../../utils/chat';
import OverlayTooltip from './UI/OverlayTooltip';
import AppDashBoardIcon from '../../assets/icon-app-dashboard.svg?react';
import SkillDashboardIcon from '@/assets/icon-skill.svg?react';
import PromptboardIcon from '@/assets/icon-saved-prompts.svg?react';
import useElysiaStore from '@/app/elysia-store';
import {
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_GEN_RESOLUTION,
  ELYSIA_PORTAL_LINK,
  SERVICE_NOW_LINK,
} from '@/config/settings';
import { SettingContext } from '@/context/SettingContext';
import { CommonDropdown } from './UI/CommonDropdown';
import { AuthContext } from '@/context/AuthContext';
import { Button } from 'react-bootstrap';
import ContentIcon from '../../assets/icon-manage-content-profile.svg?react';
import FeedbackIcon from '../../assets/icon-profile-feedback.svg?react';
import RaiseIcon from '../../assets/icon-raise-issue-profile.svg?react';
import ElysiaProtalIcon from '../../assets/icon-elysia-protal.png';

const Sidebar = () => {
  const { isSideNavOpen, setIsSideNavOpen } = useContext(AppContext);
  const { defaultModel, setSelectedModel } = useContext(SettingContext);

  const { userInitials = '' } = useContext(AuthContext);
  const { allowedRoutes = [] } = useContext(AppContext);

  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setIsRestrictedAppStatus = useElysiaStore.getState().setIsRestrictedAppStatus;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setWordsCount = useElysiaStore.getState().setWordsCount;
  const setWordCountError = useElysiaStore.getState().setWordCountError;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setIsAddSourcesActive = useElysiaStore.getState().setIsAddSourcesActive;
  const resetAIApp = useElysiaStore.getState().resetAIApp;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const setProfitCenter = useElysiaStore.getState().setProfitCenter;
  const setFinanceAppLocation = useElysiaStore.getState().setFinanceAppLocation;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const setAttachfileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const setChatInputSources = useElysiaStore((state) => state.setChatInputSources);

  const navigate = useNavigate();

  const startNewTopicChat = () => {
    setIsAddSourcesActive?.(false);
    setShowScrollIcon?.(false);
    useElysiaStore.setState({ isStreamingStopped: false });
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    setChatAgent?.('Generic');
    resetAIApp();
    setBrandDoc(false);
    setIsAppTerminated(true);
    setSelectedModel(defaultModel);
    setIsChatActive?.(false);
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    navigate('/', { replace: true });
    setPromptInput('');
    setSessionPath?.('');
    setIsRestrictedChatSessionStatus?.('');
    setIsRestrictedAppStatus('');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setAttachfileArray?.([]);
    setChatInputSources?.([]);
    setSources?.([]);
    setSelectedSources?.([]);
    setBrandDoc(false);
    setWordsCount?.(0);
    setWordCountError?.(false);
    setShowFileToasts?.(false);
    setIsChatDisabled?.(false);
    setImageGenPayload({
      aspect_ratio: DEFAULT_ASPECT_RATIO,
      color_palette: [],
      // negative_prompt: '',
      starter_image_id: '',
      image_quality: DEFAULT_IMAGE_GEN_RESOLUTION,
      starter_image_file_name: '',
    });
    setCategoryCount({});
    setProfitCenter({ value: '', label: '', level: 0, id: '' });
    setFinanceAppLocation({ value: '', label: '', level: 0, id: '' });
  };

  const handleAppDashboard = () => {
    navigate('/app-store');
  };

  const handleSkillDashboad = () => {
    navigate('/skill-store');
  };

  const handlePromptDashboad = () => {
    navigate('/prompt-store');
  };

  const isSandboxRouteAllowed = allowedRoutes?.includes('/sandbox');

  const config = {
    logoutMenu: {
      icon: <div className="user-name-initials">{userInitials}</div>,
      tooltipText: 'User Menu',
      ariaLabel: userInitials,
      testId: 'user-credentials-dropdown',
      isUserInitials: true,
      className: 'user-name-icon me-3',
      dropdownOpenClassName: 'logout-dropdown-open-state',
      displayTooltip: false,
      dropdownItems: [
        {
          key: '1',
          eventKey: '1',
          ariaLabel: 'view profile',
          dataTestId: 'view-profile-dropdown-item',
          onClick: () => navigate('/myprofile'),
          icon: <ProfileIcon />,
          text: 'Profile',
          role: 'menuitem',
          component: 'button',
        },
        {
          key: '2',
          eventKey: '2',
          ariaLabel: 'view settings',
          dataTestId: 'view-settings',
          onClick: () => navigate('/mysettings'),
          icon: <SettingsIcon />,
          text: 'Settings',
          role: 'menuitem',
          component: 'button',
        },
        {
          key: '3',
          eventKey: '3',
          ariaLabel: 'notification centre',
          dataTestId: 'view-notifications',
          onClick: () => navigate('/notifications'),
          icon: <BellIcon />,
          text: 'Notifications',
          role: 'menuitem',
          component: 'button',
        },
        {
          key: '4',
          eventKey: '4',
          ariaLabel: 'content',
          dataTestId: 'manage-content-dropdown-item',
          onClick: () => navigate('/mycontent'),
          icon: <ContentIcon />,
          text: 'Content',
          role: 'menuitem',
          component: 'button',
        },
        ...(isSandboxRouteAllowed
          ? [
              {
                key: '4',
                eventKey: '4',
                ariaLabel: 'sandbox',
                dataTestId: 'view-sandbox-dropdown-item',
                onClick: () => navigate('/sandbox'),
                icon: <SandboxIcon />,
                text: 'Sandbox',
                role: 'menuitem',
                component: 'button',
              },
            ]
          : []),
        {
          key: '5',
          eventKey: '5',
          ariaLabel: 'Please provide feedback',
          dataTestId: 'feedback-dropdown-item',
          href: 'https://forms.office.com/r/1XNySQ3xtv',
          target: '_blank',
          icon: <FeedbackIcon />,
          text: 'Provide Feedback',
          role: 'menuitem',
        },
        {
          key: '6',
          eventKey: '6',
          ariaLabel: 'Raise an issue',
          dataTestId: 'contact-service-now-dropdown-item',
          href: SERVICE_NOW_LINK,
          target: '_blank',
          icon: <RaiseIcon />,
          text: 'Raise an Issue',
          role: 'menuitem',
        },
        {
          key: '7',
          eventKey: '7',
          ariaLabel: 'Elyisa on protal',
          dataTestId: 'contact-service-now-dropdown-item',
          href: ELYSIA_PORTAL_LINK,
          target: '_blank',
          icon: <img src={ElysiaProtalIcon} alt="elysia-portal" />,
          text: 'Elysia on Portal',
          role: 'menuitem',
        },
        {
          key: '8',
          eventKey: '8',
          ariaLabel: 'logout',
          dataTestId: 'logout-dropdown-item',
          onClick: logout,
          icon: <LogoutIcon />,
          text: 'Log Out',
          role: 'menuitem',
          component: 'button',
        },
      ].filter(Boolean),
    },
  };

  return (
    <>
      <ChatHistorySidebar startNewTopicChat={startNewTopicChat} config={config} />
      <div className={`btn-container d-flex flex-column h-100 ${isSideNavOpen ? 'd-none' : ''}`}>
        <div>
          <div className="sidebar-brain-wrapper position-relative">
            <Button className="sidebar-top-icon" onClick={() => setIsSideNavOpen(!isSideNavOpen)}>
              <ElysiaBrainIcon />
            </Button>

            <OverlayTooltip
              tooltipText="Open Side Panel"
              buttonContent={<OpenLeftSidePanelIcon />}
              ariaLabel="open side panel"
              testId="open-side-panel-button"
              tooltipClassName="tooltip-sidebar"
              buttonClassName="history-icon-wrapper fw-semibold sidebar-hover-button position-absolute top-0 start-0"
              onClick={() => setIsSideNavOpen(!isSideNavOpen)}
            />
          </div>

          <OverlayTooltip
            tooltipText="New Chat"
            buttonContent={<NewChatIcon />}
            ariaLabel="new chat"
            testId="new-chat-icon-button"
            tooltipClassName="tooltip-sidebar"
            buttonClassName="history-icon-wrapper fw-semibold"
            onClick={startNewTopicChat}
          />

          <OverlayTooltip
            tooltipText="AI App Store"
            buttonContent={<AppDashBoardIcon />}
            ariaLabel="AI app store"
            testId="apps-dashboard-button"
            tooltipClassName="tooltip-sidebar"
            buttonClassName="dashboard-icon-wrapper fw-semibold"
            onClick={handleAppDashboard}
          />

          <OverlayTooltip
            tooltipText="AI Skill Store"
            buttonContent={<SkillDashboardIcon />}
            ariaLabel="AI Skill store"
            testId="skill-dashboard-button"
            tooltipClassName="tooltip-sidebar"
            buttonClassName="dashboard-icon-wrapper fw-semibold"
            onClick={handleSkillDashboad}
          />

          <OverlayTooltip
            tooltipText="AI Prompt Store"
            buttonContent={<PromptboardIcon />}
            ariaLabel="AI Prompt store"
            testId="prompt-dashboard-button"
            tooltipClassName="tooltip-sidebar"
            buttonClassName="dashboard-icon-wrapper fw-semibold"
            onClick={handlePromptDashboad}
          />
        </div>

        <div className="mt-auto">
          <CommonDropdown {...config.logoutMenu} />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
